import { useCallback } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from '@hello-pangea/dnd';
import { DragHandle as MaterialDragHandle } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const DragHandle = styled(MaterialDragHandle)`
  position: absolute;
  top: 0;
  right: 16px;
  bottom: 0;
  margin: auto 0;
`;

export interface ReorderItem {
  id: string;
  title: string;
}

interface ReorderListProps {
  items: ReorderItem[];
  onReorder: (items: ReorderItem[]) => void;
}

export const ReorderList = ({ items, onReorder }: ReorderListProps) => {
  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;

      const reordered = [...items];
      const [removed] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, removed);

      onReorder(reordered);
    },
    [items, onReorder]
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {items.map((item, index) => (
              <Draggable key={item.id} draggableId={item.id} index={index}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                  >
                    <Box
                      sx={(theme) => ({
                        height: '40px',
                        position: 'relative',
                        border: `1px solid ${theme.palette.neutral[300]}`,
                        py: 1,
                        px: 2,
                        backgroundColor: theme.palette.background.paper,
                      })}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          width: '90%',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index + 1}. {item.title}
                      </Typography>
                      <DragHandle />
                    </Box>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
